import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import useReveal from '../hooks/useReveal';
import SEO from '../components/SEO';
export default function BlogDjangoVsFastAPI() {
  useReveal();
  useEffect(() => { window.scrollTo({ top: 0, behavior: 'instant' }); }, []);

  return (
    <div className="page">
      <SEO
  title="Django vs FastAPI: Choosing the Right Backend Framework | Arhatinfo"
  description="Django or FastAPI — which Python backend framework should you choose? A practical comparison covering architecture, performance, use cases, and when to use both together."
  keywords="Django vs FastAPI, Python backend framework, Django REST framework, FastAPI comparison, Python web framework, Django FastAPI difference, backend framework 2025"
  canonical="https://arhatinfo.com/blog/django-vs-fastapi"
  og={{
    title:       "Django vs FastAPI: Choosing the Right Python Backend Framework",
    description: "Django gives structure. FastAPI gives speed and flexibility. A practical guide to choosing the right framework — or using both together.",
    url:         "https://arhatinfo.com/blog/django-vs-fastapi",
    image:       "https://arhatinfo.com/og-blog.png",
  }}
/>
      <article>

        {/* ── HERO ─────────────────────────────────────────────────── */}
        <div style={{
          background: 'linear-gradient(135deg,var(--bg2),var(--bg3))',
          borderBottom: '1px solid var(--border-s)',
          padding: '80px 5vw 60px',
          position: 'relative', overflow: 'hidden',
        }}>
          <div style={{
            position:'absolute',inset:0,
            backgroundImage:'linear-gradient(rgba(53,187,234,.04) 1px,transparent 1px),linear-gradient(90deg,rgba(53,187,234,.04) 1px,transparent 1px)',
            backgroundSize:'50px 50px',pointerEvents:'none',
          }}/>
          <div className="wrap" style={{ position:'relative', zIndex:1 }}>
            <div style={{ display:'flex',alignItems:'center',gap:'.5rem',marginBottom:'1.5rem',fontSize:'.8rem',fontWeight:500 }}>
              <Link to="/"     style={{ color:'var(--muted)',textDecoration:'none' }}>Home</Link>
              <span style={{ color:'var(--muted)' }}>›</span>
              <Link to="/blog" style={{ color:'var(--muted)',textDecoration:'none' }}>Blog</Link>
              <span style={{ color:'var(--muted)' }}>›</span>
              <span style={{ color:'var(--blue-hi)' }}>Django vs FastAPI</span>
            </div>

            <div style={{ display:'flex',alignItems:'center',gap:'.75rem',marginBottom:'1.2rem',flexWrap:'wrap' }}>
              <span style={{
                fontSize:'.72rem',fontWeight:700,letterSpacing:'.12em',textTransform:'uppercase',
                color:'var(--blue-hi)',background:'rgba(53,187,234,.1)',
                border:'1px solid rgba(53,187,234,.25)',padding:'.28rem .9rem',borderRadius:20,
              }}>Framework Comparison</span>
              <span style={{ fontSize:'.78rem',fontWeight:500,color:'var(--muted)' }}>9 min read</span>
              <span style={{ color:'var(--muted)',fontSize:'.78rem' }}>·</span>
              <span style={{ fontSize:'.78rem',fontWeight:500,color:'var(--muted)' }}>May 2025</span>
            </div>

            <h1 style={{
              fontFamily:'var(--font)',fontSize:'clamp(1.9rem,4.5vw,3.2rem)',
              fontWeight:900,color:'var(--white)',lineHeight:1.1,letterSpacing:'-.02em',
              maxWidth:820,marginBottom:'1.4rem',textShadow:'0 4px 20px rgba(0,0,0,.25)',
            }}>
              Django vs FastAPI:{' '}
              <span style={{ color:'var(--blue-hi)' }}>Choosing the Right Backend Framework</span>
            </h1>

            <div style={{ display:'flex',alignItems:'center',gap:'.75rem' }}>
              <div style={{
                width:40,height:40,borderRadius:'50%',flexShrink:0,
                background:'linear-gradient(135deg,var(--blue-dark),var(--blue-deep))',
                border:'2px solid var(--blue-hi)',
                display:'flex',alignItems:'center',justifyContent:'center',
                fontSize:'.78rem',fontWeight:700,color:'var(--white)',
              }}>RJ</div>
              <div>
                <div style={{ fontSize:'.85rem',fontWeight:700,color:'var(--white)' }}>Rohit Jain</div>
                <div style={{ fontSize:'.75rem',fontWeight:500,color:'var(--muted)' }}>arhatinfo.com · Backend & AI Engineering</div>
              </div>
            </div>
          </div>
        </div>

        {/* ── BODY ─────────────────────────────────────────────────── */}
        <div style={{ padding:'60px 5vw 80px' }}>
          <div style={{ maxWidth:780, margin:'0 auto' }}>

            <Lead>
              Both Django and FastAPI are powerful Python backend frameworks, but they solve
              different problems. Django is best when you want to build a complete business
              application quickly. FastAPI is best when you want to build lightweight,
              high-performance APIs, microservices, or AI-powered backend services.
            </Lead>
            <Lead>
              The right choice depends less on popularity and more on your product architecture.
            </Lead>

            {/* ── DIAGRAM 1 ── */}
            <div className="rv" style={{ margin:'2.5rem 0', borderRadius:'var(--r)', overflow:'hidden', border:'.5px solid var(--border-hi)', boxShadow:'2px 10px 30px var(--shadow)' }}>
              <svg width="100%" height="420" viewBox="0 0 900 420" xmlns="http://www.w3.org/2000/svg">
                <rect width="900" height="420" rx="24" fill="#0c0e14"/>
                <text x="450" y="50" textAnchor="middle" fontSize="22" fontFamily="Arial" fill="#eef2ff" fontWeight="700">Django vs FastAPI — At a Glance</text>
                {/* Django card */}
                <rect x="60" y="80" width="340" height="270" rx="16" fill="#111520" stroke="#35BBEA" strokeWidth="2"/>
                <text x="230" y="122" textAnchor="middle" fontSize="22" fontFamily="Arial" fill="#35BBEA" fontWeight="700">Django</text>
                <text x="90" y="162" fontSize="15" fontFamily="Arial" fill="#c8d6e5">✓  Full-stack business apps</text>
                <text x="90" y="197" fontSize="15" fontFamily="Arial" fill="#c8d6e5">✓  Admin panel included</text>
                <text x="90" y="232" fontSize="15" fontFamily="Arial" fill="#c8d6e5">✓  ORM, auth, sessions, forms</text>
                <text x="90" y="267" fontSize="15" fontFamily="Arial" fill="#c8d6e5">✓  Best for SaaS, CRM, ERP</text>
                <rect x="90" y="295" width="220" height="34" rx="8" fill="#35BBEA" fillOpacity="0.12"/>
                <text x="200" y="317" textAnchor="middle" fontSize="13" fontFamily="Arial" fill="#35BBEA">Batteries included</text>
                {/* FastAPI card */}
                <rect x="500" y="80" width="340" height="270" rx="16" fill="#111520" stroke="#4ade80" strokeWidth="2"/>
                <text x="670" y="122" textAnchor="middle" fontSize="22" fontFamily="Arial" fill="#4ade80" fontWeight="700">FastAPI</text>
                <text x="530" y="162" fontSize="15" fontFamily="Arial" fill="#c8d6e5">✓  API-first architecture</text>
                <text x="530" y="197" fontSize="15" fontFamily="Arial" fill="#c8d6e5">✓  Async and high performance</text>
                <text x="530" y="232" fontSize="15" fontFamily="Arial" fill="#c8d6e5">✓  Auto OpenAPI docs</text>
                <text x="530" y="267" fontSize="15" fontFamily="Arial" fill="#c8d6e5">✓  Best for AI, ML, microservices</text>
                <rect x="530" y="295" width="220" height="34" rx="8" fill="#4ade80" fillOpacity="0.12"/>
                <text x="640" y="317" textAnchor="middle" fontSize="13" fontFamily="Arial" fill="#4ade80">API-first · Async</text>
                {/* footer */}
                <text x="450" y="390" textAnchor="middle" fontSize="16" fontFamily="Arial" fill="#6A99AB">Django gives structure. FastAPI gives speed and flexibility.</text>
              </svg>
            </div>

            {/* core diff */}
            <SectionHeading>The Core Difference</SectionHeading>
            <Body>
              Django is a <Strong>batteries-included framework</Strong>. It gives you almost
              everything needed to build a complete backend application — ORM, authentication,
              admin dashboard, permissions, sessions, migrations, and security features.
            </Body>
            <Body>
              FastAPI is an <Strong>API-first framework</Strong>. It gives you a clean, fast,
              modern way to build APIs using Python type hints and async support.
            </Body>

            <div className="rv" style={{
              display:'grid', gridTemplateColumns:'1fr 1fr', gap:'1rem', margin:'1.8rem 0',
            }}>
              <Verdict color="#35BBEA" label="Choose Django when" text="your product needs business workflows, admin panels, and a full backend platform." />
              <Verdict color="#4ade80" label="Choose FastAPI when" text="your product needs fast APIs, async processing, microservices, or AI services." />
            </div>

            {/* Django section */}
            <SectionHeading>Django: Best for Complete Business Applications</SectionHeading>
            <Body>
              Django is excellent when you need to build a full backend system with many standard
              business features already solved. It helps teams move fast because the common
              problems are already handled out of the box.
            </Body>
            <Body>Django is perfect for:</Body>
            <BulletList items={['SaaS platforms','Internal tools and CRM systems','ERP and marketplace applications','Admin-heavy applications','Content management systems','Rapid MVP development']} />

            {/* FastAPI section */}
            <SectionHeading>FastAPI: Best for APIs, Microservices, and AI Systems</SectionHeading>
            <Body>
              FastAPI is designed for modern API development. It is lightweight, fast, and
              developer-friendly. It works especially well when your backend is mostly APIs
              consumed by frontend apps, mobile apps, ML systems, or other services.
            </Body>
            <Body>FastAPI is great for:</Body>
            <BulletList items={['AI and ML model serving APIs','RAG pipelines and vector search APIs','Microservices and real-time systems','WebSocket and IoT backends','High-concurrency APIs','Async data processing services']} />

            {/* ── DIAGRAM 2 ── */}
            <SectionHeading>Architecture Difference</SectionHeading>
            <div className="rv" style={{ margin:'1.5rem 0', borderRadius:'var(--r)', overflow:'hidden', border:'.5px solid var(--border-hi)', boxShadow:'2px 10px 30px var(--shadow)' }}>
              <svg width="100%" height="460" viewBox="0 0 950 460" xmlns="http://www.w3.org/2000/svg">
                <rect width="950" height="460" rx="24" fill="#0c0e14"/>
                <text x="475" y="46" textAnchor="middle" fontSize="22" fontFamily="Arial" fill="#eef2ff" fontWeight="700">Architecture Difference</text>
                {/* Django side */}
                <rect x="55" y="75" width="380" height="320" rx="18" fill="#111520" stroke="#35BBEA" strokeWidth="2"/>
                <text x="245" y="116" textAnchor="middle" fontSize="20" fontFamily="Arial" fill="#35BBEA" fontWeight="700">Django Monolith</text>
                <rect x="100" y="140" width="270" height="44" rx="10" fill="#35BBEA" fillOpacity="0.15"/>
                <text x="235" y="167" textAnchor="middle" fontSize="14" fontFamily="Arial" fill="#eef2ff">Frontend / Templates / API</text>
                <line x1="235" y1="184" x2="235" y2="200" stroke="#35BBEA" strokeWidth="1.5" strokeOpacity="0.4"/>
                <rect x="100" y="200" width="270" height="44" rx="10" fill="#35BBEA" fillOpacity="0.22"/>
                <text x="235" y="227" textAnchor="middle" fontSize="14" fontFamily="Arial" fill="#eef2ff">Business Logic</text>
                <line x1="235" y1="244" x2="235" y2="260" stroke="#35BBEA" strokeWidth="1.5" strokeOpacity="0.4"/>
                <rect x="100" y="260" width="270" height="44" rx="10" fill="#35BBEA" fillOpacity="0.30"/>
                <text x="235" y="287" textAnchor="middle" fontSize="14" fontFamily="Arial" fill="#eef2ff">ORM + Auth + Admin</text>
                <line x1="235" y1="304" x2="235" y2="324" stroke="#35BBEA" strokeWidth="1.5" strokeOpacity="0.4"/>
                <rect x="140" y="324" width="190" height="38" rx="10" fill="#35BBEA"/>
                <text x="235" y="348" textAnchor="middle" fontSize="14" fontFamily="Arial" fill="#001B26" fontWeight="700">Database</text>
                {/* FastAPI side */}
                <rect x="515" y="75" width="380" height="320" rx="18" fill="#111520" stroke="#4ade80" strokeWidth="2"/>
                <text x="705" y="116" textAnchor="middle" fontSize="20" fontFamily="Arial" fill="#4ade80" fontWeight="700">FastAPI Services</text>
                <rect x="560" y="148" width="130" height="52" rx="10" fill="#4ade80" fillOpacity="0.15"/>
                <text x="625" y="178" textAnchor="middle" fontSize="14" fontFamily="Arial" fill="#eef2ff">API Service</text>
                <rect x="710" y="148" width="130" height="52" rx="10" fill="#4ade80" fillOpacity="0.15"/>
                <text x="775" y="178" textAnchor="middle" fontSize="14" fontFamily="Arial" fill="#eef2ff">AI Service</text>
                <line x1="625" y1="200" x2="625" y2="220" stroke="#4ade80" strokeWidth="1.5" strokeOpacity="0.4"/>
                <line x1="775" y1="200" x2="775" y2="220" stroke="#4ade80" strokeWidth="1.5" strokeOpacity="0.4"/>
                <rect x="560" y="220" width="130" height="52" rx="10" fill="#4ade80" fillOpacity="0.22"/>
                <text x="625" y="250" textAnchor="middle" fontSize="14" fontFamily="Arial" fill="#eef2ff">Auth Layer</text>
                <rect x="710" y="220" width="130" height="52" rx="10" fill="#4ade80" fillOpacity="0.22"/>
                <text x="775" y="250" textAnchor="middle" fontSize="14" fontFamily="Arial" fill="#eef2ff">Worker</text>
                <line x1="625" y1="272" x2="705" y2="308" stroke="#4ade80" strokeWidth="1.5" strokeOpacity="0.4"/>
                <line x1="775" y1="272" x2="705" y2="308" stroke="#4ade80" strokeWidth="1.5" strokeOpacity="0.4"/>
                <rect x="600" y="308" width="210" height="38" rx="10" fill="#4ade80"/>
                <text x="705" y="332" textAnchor="middle" fontSize="14" fontFamily="Arial" fill="#001B26" fontWeight="700">Database / Queue</text>
                {/* footer */}
                <text x="475" y="425" textAnchor="middle" fontSize="15" fontFamily="Arial" fill="#6A99AB">Django centralizes features. FastAPI separates services.</text>
              </svg>
            </div>

            {/* performance */}
            <SectionHeading>Performance Comparison</SectionHeading>
            <Body>
              FastAPI is generally faster for high-concurrency workloads because it was designed
              around async request handling. Django is also production-ready and scalable, but
              many Django systems are more database-bound than framework-bound.
            </Body>
            <Callout>
              A well-designed Django system can outperform a poorly designed FastAPI system.
              Performance depends on database design, caching, query optimization, and deployment
              architecture — not just the framework choice.
            </Callout>

            {/* when to choose */}
            <SectionHeading>When to Choose Each Framework</SectionHeading>

            {/* ── DIAGRAM 3 ── */}
            <div className="rv" style={{ margin:'1.5rem 0', borderRadius:'var(--r)', overflow:'hidden', border:'.5px solid var(--border-hi)', boxShadow:'2px 10px 30px var(--shadow)' }}>
              <svg width="100%" height="480" viewBox="0 0 950 480" xmlns="http://www.w3.org/2000/svg">
                <rect width="950" height="480" rx="24" fill="#0c0e14"/>
                <text x="475" y="46" textAnchor="middle" fontSize="22" fontFamily="Arial" fill="#eef2ff" fontWeight="700">When Should You Use Django or FastAPI?</text>
                <line x1="475" y1="78" x2="475" y2="420" stroke="#243D4D" strokeWidth="2"/>
                <text x="235" y="90" textAnchor="middle" fontSize="20" fontFamily="Arial" fill="#35BBEA" fontWeight="700">Use Django</text>
                <text x="715" y="90" textAnchor="middle" fontSize="20" fontFamily="Arial" fill="#4ade80" fontWeight="700">Use FastAPI</text>
                {[
                  'Admin-heavy apps','SaaS platforms','CRM / ERP systems','Business workflows','Rapid MVP development',
                ].map((item, i) => (
                  <g key={i}>
                    <circle cx="100" cy={145 + i*54} r="9" fill="#35BBEA"/>
                    <text x="122" y={150 + i*54} fontSize="16" fontFamily="Arial" fill="#c8d6e5">{item}</text>
                  </g>
                ))}
                {[
                  'AI / ML APIs','Microservices','Async workloads','Realtime systems','High-concurrency APIs',
                ].map((item, i) => (
                  <g key={i}>
                    <circle cx="560" cy={145 + i*54} r="9" fill="#4ade80"/>
                    <text x="582" y={150 + i*54} fontSize="16" fontFamily="Arial" fill="#c8d6e5">{item}</text>
                  </g>
                ))}
                <rect x="200" y="428" width="550" height="40" rx="14" fill="#243D4D" stroke="#35BBEA" strokeWidth="1.5"/>
                <text x="475" y="453" textAnchor="middle" fontSize="16" fontFamily="Arial" fill="#35BBEA" fontWeight="700">Many modern systems use both together.</text>
              </svg>
            </div>

            {/* using both */}
            <SectionHeading>When to Use Both Django and FastAPI</SectionHeading>
            <Body>
              In real-world systems, Django and FastAPI can work together very well. A common
              architecture is Django handling the core business platform while FastAPI handles
              specialized API services.
            </Body>

            <div className="rv" style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'1rem', margin:'1.5rem 0' }}>
              <div style={{ background:'rgba(53,187,234,.06)', border:'.5px solid rgba(53,187,234,.3)', borderRadius:'var(--r)', padding:'1.3rem' }}>
                <div style={{ fontSize:'.72rem', fontWeight:700, letterSpacing:'.12em', textTransform:'uppercase', color:'var(--blue-hi)', marginBottom:'.9rem' }}>Django manages</div>
                {['Users & billing','Admin dashboard','Permissions & reports','Business data','Workflows'].map(f => (
                  <div key={f} style={{ display:'flex', gap:'.5rem', fontSize:'.83rem', fontWeight:500, color:'var(--white-70)', marginBottom:'.35rem' }}>
                    <span style={{ color:'var(--blue-hi)' }}>→</span>{f}
                  </div>
                ))}
              </div>
              <div style={{ background:'rgba(74,222,128,.05)', border:'.5px solid rgba(74,222,128,.25)', borderRadius:'var(--r)', padding:'1.3rem' }}>
                <div style={{ fontSize:'.72rem', fontWeight:700, letterSpacing:'.12em', textTransform:'uppercase', color:'#4ade80', marginBottom:'.9rem' }}>FastAPI manages</div>
                {['AI model APIs','Search APIs','Async integrations','WebSocket services','Data processing'].map(f => (
                  <div key={f} style={{ display:'flex', gap:'.5rem', fontSize:'.83rem', fontWeight:500, color:'var(--white-70)', marginBottom:'.35rem' }}>
                    <span style={{ color:'#4ade80' }}>→</span>{f}
                  </div>
                ))}
              </div>
            </div>

            {/* ── DIAGRAM 4 ── */}
            <SectionHeading>Hybrid Architecture Diagram</SectionHeading>
            <div className="rv" style={{ margin:'1.5rem 0', borderRadius:'var(--r)', overflow:'hidden', border:'.5px solid var(--border-hi)', boxShadow:'2px 10px 30px var(--shadow)' }}>
              <svg width="100%" height="500" viewBox="0 0 1000 500" xmlns="http://www.w3.org/2000/svg">
                <rect width="1000" height="500" rx="24" fill="#0c0e14"/>
                <text x="500" y="46" textAnchor="middle" fontSize="22" fontFamily="Arial" fill="#eef2ff" fontWeight="700">Hybrid Backend Architecture</text>
                {/* client */}
                <rect x="380" y="72" width="240" height="50" rx="12" fill="#243D4D"/>
                <text x="500" y="102" textAnchor="middle" fontSize="16" fontFamily="Arial" fill="#eef2ff">React / Mobile App</text>
                <line x1="500" y1="122" x2="500" y2="158" stroke="#35BBEA" strokeWidth="2" strokeOpacity="0.5"/>
                {/* gateway */}
                <rect x="370" y="158" width="260" height="50" rx="12" fill="#20303D" stroke="#35BBEA" strokeWidth="1.5"/>
                <text x="500" y="188" textAnchor="middle" fontSize="16" fontFamily="Arial" fill="#35BBEA" fontWeight="700">API Gateway</text>
                <line x1="420" y1="208" x2="270" y2="268" stroke="#35BBEA" strokeWidth="2" strokeOpacity="0.4"/>
                <line x1="580" y1="208" x2="730" y2="268" stroke="#4ade80" strokeWidth="2" strokeOpacity="0.4"/>
                {/* Django box */}
                <rect x="80" y="268" width="370" height="140" rx="16" fill="#111520" stroke="#35BBEA" strokeWidth="2"/>
                <text x="265" y="306" textAnchor="middle" fontSize="19" fontFamily="Arial" fill="#35BBEA" fontWeight="700">Django Core Platform</text>
                <text x="115" y="340" fontSize="14" fontFamily="Arial" fill="#8b96b0">Users  ·  Auth  ·  Billing</text>
                <text x="115" y="368" fontSize="14" fontFamily="Arial" fill="#8b96b0">Admin  ·  Reports  ·  Workflows</text>
                {/* FastAPI box */}
                <rect x="550" y="268" width="370" height="140" rx="16" fill="#111520" stroke="#4ade80" strokeWidth="2"/>
                <text x="735" y="306" textAnchor="middle" fontSize="19" fontFamily="Arial" fill="#4ade80" fontWeight="700">FastAPI Services</text>
                <text x="585" y="340" fontSize="14" fontFamily="Arial" fill="#8b96b0">AI APIs  ·  Search  ·  WebSockets</text>
                <text x="585" y="368" fontSize="14" fontFamily="Arial" fill="#8b96b0">Async Jobs  ·  ML Inference</text>
                <line x1="265" y1="408" x2="500" y2="440" stroke="#35BBEA" strokeWidth="2" strokeOpacity="0.4"/>
                <line x1="735" y1="408" x2="500" y2="440" stroke="#4ade80" strokeWidth="2" strokeOpacity="0.4"/>
                {/* shared db */}
                <rect x="365" y="440" width="270" height="44" rx="12" fill="#20303D" stroke="#6A99AB" strokeWidth="1.5"/>
                <text x="500" y="467" textAnchor="middle" fontSize="15" fontFamily="Arial" fill="#6A99AB" fontWeight="700">PostgreSQL  ·  Redis  ·  Queue</text>
              </svg>
            </div>

            {/* final recommendation */}
            <SectionHeading>Final Recommendation</SectionHeading>

            <div className="rv" style={{
              display:'grid', gridTemplateColumns:'1fr', gap:'1rem', margin:'1.5rem 0',
            }}>
              {[
                { color:'#35BBEA', icon:'🏢', when:'Use Django when', desc:'You need a complete backend application with strong business logic, admin functionality, user management, and a full platform.' },
                { color:'#4ade80', icon:'⚡', when:'Use FastAPI when', desc:'You need fast, modern APIs, async processing, microservices, or AI-driven services with automatic documentation.' },
                { color:'#f59e0b', icon:'🔗', when:'Use both when', desc:'Your system has a business platform plus specialized high-performance services — Django handles the core, FastAPI handles the APIs.' },
              ].map((r, i) => (
                <div key={i} style={{
                  background:'var(--bg2)', border:`.5px solid ${r.color}55`,
                  borderLeft:`3px solid ${r.color}`,
                  borderRadius:'var(--r)', padding:'1.2rem 1.4rem',
                  display:'flex', alignItems:'flex-start', gap:'1rem',
                }}>
                  <span style={{ fontSize:'1.3rem', flexShrink:0 }}>{r.icon}</span>
                  <div>
                    <div style={{ fontSize:'.82rem', fontWeight:700, letterSpacing:'.1em', textTransform:'uppercase', color:r.color, marginBottom:'.35rem' }}>{r.when}</div>
                    <div style={{ fontSize:'.88rem', fontWeight:500, color:'var(--white-70)', lineHeight:1.65 }}>{r.desc}</div>
                  </div>
                </div>
              ))}
            </div>

            <Callout>
              The framework is not the real architecture decision. The real decision is: are you
              building a business application, an API service, or a system that needs both?
            </Callout>

            {/* author bio */}
            <div className="rv" style={{
              display:'flex', gap:'1.2rem', alignItems:'flex-start',
              padding:'1.5rem', background:'var(--bg2)',
              border:'.5px solid var(--border-hi)', borderRadius:'var(--r)',
              boxShadow:'2px 10px 20px var(--shadow)', marginBottom:'3rem',
            }}>
              <div style={{
                width:52, height:52, borderRadius:'50%', flexShrink:0,
                background:'linear-gradient(135deg,var(--blue-dark),var(--blue-deep))',
                border:'2px solid var(--blue-hi)',
                display:'flex', alignItems:'center', justifyContent:'center',
                fontSize:'.88rem', fontWeight:700, color:'var(--white)',
              }}>RJ</div>
              <div>
                <div style={{ fontSize:'.92rem', fontWeight:700, color:'var(--white)', marginBottom:'.25rem' }}>Written by Rohit Jain</div>
                <div style={{ fontSize:'.8rem', fontWeight:500, color:'var(--muted)', marginBottom:'.6rem' }}>Founder, arhatinfo.com · Backend Engineer · AI Automation Specialist</div>
                <div style={{ fontSize:'.83rem', fontWeight:500, color:'var(--white-70)', lineHeight:1.7 }}>
                  Building scalable backend systems, automation platforms, cloud-native applications, and AI-driven software solutions.
                </div>
              </div>
            </div>

            {/* CTA */}
            <div className="rv" style={{ textAlign:'center' }}>
              <div style={{ fontSize:'.82rem', fontWeight:500, color:'var(--muted)', marginBottom:'1rem' }}>
                Not sure which framework is right for your project?
              </div>
              <Link to="/contact" className="btn-p">Book a Free Consultation →</Link>
            </div>

          </div>
        </div>
      </article>
    </div>
  );
}

// ── SHARED COMPONENTS ─────────────────────────────────────────────────

function Lead({ children }) {
  return <p style={{ fontSize:'1.05rem', fontWeight:500, color:'rgba(235,242,255,0.88)', lineHeight:1.8, marginBottom:'1.2rem' }}>{children}</p>;
}

function Body({ children }) {
  return <p style={{ fontSize:'.95rem', fontWeight:400, color:'rgba(235,242,255,0.78)', lineHeight:1.85, marginBottom:'1rem' }}>{children}</p>;
}

function Strong({ children }) {
  return <strong style={{ color:'var(--white)', fontWeight:700 }}>{children}</strong>;
}

function SectionHeading({ children }) {
  return (
    <h2 style={{
      fontFamily:'var(--font)', fontSize:'clamp(1.3rem,2.5vw,1.75rem)',
      fontWeight:900, color:'var(--white)',
      marginTop:'2.8rem', marginBottom:'1rem', lineHeight:1.2,
      paddingBottom:'.6rem', borderBottom:'1px solid var(--border-s)',
    }}>{children}</h2>
  );
}

function Callout({ children }) {
  return (
    <blockquote style={{
      margin:'1.8rem 0', padding:'1.1rem 1.4rem',
      background:'rgba(53,187,234,.06)', borderLeft:'3px solid var(--blue-hi)',
      borderRadius:'0 var(--r) var(--r) 0',
      fontSize:'1rem', fontWeight:600, color:'var(--white)', lineHeight:1.7, fontStyle:'italic',
    }}>{children}</blockquote>
  );
}

function BulletList({ items }) {
  return (
    <ul style={{ listStyle:'none', margin:'.8rem 0 1.2rem', padding:0, display:'flex', flexDirection:'column', gap:'.45rem' }}>
      {items.map((item, i) => (
        <li key={i} style={{ display:'flex', alignItems:'flex-start', gap:'.65rem', fontSize:'.92rem', fontWeight:400, color:'rgba(235,242,255,0.78)', lineHeight:1.65 }}>
          <span style={{ color:'var(--blue-hi)', fontWeight:700, flexShrink:0, marginTop:2 }}>→</span>{item}
        </li>
      ))}
    </ul>
  );
}

function Verdict({ color, label, text }) {
  return (
    <div style={{
      background:'var(--bg2)', border:`.5px solid ${color}55`,
      borderTop:`3px solid ${color}`, borderRadius:'var(--r)', padding:'1.3rem',
    }}>
      <div style={{ fontSize:'.72rem', fontWeight:700, letterSpacing:'.12em', textTransform:'uppercase', color, marginBottom:'.5rem' }}>{label}</div>
      <div style={{ fontSize:'.88rem', fontWeight:500, color:'var(--white-70)', lineHeight:1.65 }}>{text}</div>
    </div>
  );
}